<template>
  <div class="card">
    <div class="card-header">
      <div class="row">
        <div class="col-12 col-lg">
          <h3 class="card-header-title">End of Placement Review</h3>
        </div>

        <div class="col-6 col-lg-auto text-lg-right">
          <span class="minor">
            <span class="strong">Open Date:</span>
            {{ placement.end_component_open_date | date }}
          </span>
        </div>

        <div class="col-6 col-lg-auto text-lg-right">
          <span class="minor">
            <span class="strong">Due Date:</span>
            {{ placement.end_component_due_date | date }}
          </span>
        </div>
      </div><!-- /.row -->
    </div>
    <ul class="list-data no-links no-hover">
      <li>
        <div class="row align-items-center">
          <div class="col">
            <p>Graduate Comments</p>
          </div>

          <!-- Graduate Comments Statuses for Graduates -->
          <div class="col-auto" v-if="$userIsGraduate">
            <span
              class="status status-default"
              v-if="placementEndComponentIsContextuallyNotOpen"
            >Not Open</span>
            <span
              class="status status-default"
              v-else-if="placementEndComponentIsPendingData"
            >Pending Graduate Entry</span>
            <span
              class="status status-info"
              v-else-if="placementEndComponentIsDraft"
            >Draft</span>
            <span
              class="status status-info"
              v-else-if="placementEndComponentIsPendingReview"
            >Pending Approval</span>
            <span
              class="status status-success"
              v-else-if="placementEndComponentIsSignedOff"
            >Signed Off</span>
          </div>

          <!-- Graduate Comments Statuses for Everyone Else -->
          <div class="col-auto" v-else>
            <span
              class="status status-success"
              v-if="placementEndComponentIsSignedOff"
            >Signed Off</span>
            <span
              class="status status-default"
              v-else-if="placementEndComponentIsContextuallyNotOpen"
            >Not Open</span>
            <span
              class="status status-default"
              v-else-if="placementEndComponentIsPendingData || placementEndComponentIsDraft"
            >Pending Graduate Entry</span>
            <span
              class="status status-info"
              v-else-if="placementEndComponentIsPendingReview"
            >Pending Approval</span>
          </div>
        </div>
      </li>
      <li>
        <div class="row align-items-center">
          <div class="col">
            <p>Manager Review</p>
          </div>

          <!-- Manager Review Statuses for Graduates -->
          <div class="col-auto" v-if="$userIsGraduate">
            <span
              class="status status-default"
              v-if="placementEndComponentIsContextuallyNotOpen"
            >Not Open</span>
            <span
              class="status status-default"
              v-else-if="placementEndComponentIsPendingData"
            >Pending Graduate Entry</span>
            <span
              class="status status-info"
              v-else-if="placementEndComponentIsDraft"
            >Draft</span>
            <span
              class="status status-info"
              v-else-if="placementEndComponentIsPendingReview"
            >Pending Review</span>
            <span
              class="status status-success"
              v-else-if="placementEndComponentIsSignedOff"
            >Signed Off</span>
          </div>

          <!-- Manager Review Statuses for Everyone Else -->
          <div class="col-auto" v-else>
            <span
              class="status status-success"
              v-if="placementEndComponentIsSignedOff"
            >Signed Off</span>
            <span
              class="status status-default"
              v-else-if="placementEndComponentIsContextuallyNotOpen"
            >Not Open</span>
            <span
              class="status status-default"
              v-else-if="placementEndComponentIsPendingData || placementEndComponentIsDraft"
            >Pending Graduate Entry</span>
            <span
              class="status status-info"
              v-else-if="placementEndComponentIsPendingReview"
            >Pending Review</span>
          </div>
        </div>
      </li>
    </ul><!-- /.list-data -->

    <div class="card-footer text-right" v-if="$userIsGraduate && placementEndComponentIsOpen && !placementEndComponentIsSignedOff">
      <router-link
        v-if="!placementEndComponentIsPendingData && !placementEndComponentIsDraft"
        class="btn btn-sm btn-success"
        :to="{ name: 'placements.single.end.review', params: { placementId: placement.id } }"
      >View</router-link>
      <router-link
        v-else
        class="btn btn-sm btn-success"
        :to="{ name: 'placements.single.end.objectives', params: { placementId: placement.id } }"
      >Continue</router-link>
    </div>

    <div class="card-footer text-right" v-else-if="$userIsManager && !placementEndComponentIsPendingData && !placementEndComponentIsDraft">
      <router-link
        v-if="placementEndComponentCapabilityAssessmentComplete && placementEndComponentPerformanceObjectivesComplete && placementEndComponentOverallAssessmentComplete"
        class="btn btn-sm btn-success"
        :to="{ name: 'placements.single.end.manager.review', params: { placementId: placement.id, graduateId: placement.graduate_id } }"
      >View</router-link>
      <router-link
        v-else
        class="btn btn-sm btn-success"
        :to="{ name: 'placements.single.end.manager.performance', params: { placementId: placement.id, graduateId: placement.graduate_id } }"
      >Continue</router-link>
    </div>

    <div class="card-footer text-right" v-if="$userIsAdmin && placementEndComponentIsOpen && !placementEndComponentIsSignedOff">
      <router-link
        v-if="placementEndComponentIsPendingData || placementEndComponentIsDraft"
        class="btn btn-sm btn-success"
        :to="{ name: 'placements.single.end.admin.objectives', params: { placementId: placement.id, graduateId: placement.graduate_id } }"
      >Continue as graduate</router-link>

      <ladda-button
        v-if="$userIsAdmin && placementEndComponentIsPendingReview && placementStartComponentIsSignedOff"
        :click="handleApprovePlacement" class="btn btn-sm btn-warning">
        Admin Override - Set As Finalised
      </ladda-button>
    </div>

    <div class="card-footer text-right" v-if="$userIsAdmin && placementEndComponentIsSignedOff">
      <ladda-button
        class="btn btn-sm btn-danger"
        :click="handleRevertPlacement"
      >
        Revert Placement
      </ladda-button>
    </div>

    <div class="card-footer text-right" v-if="$userIsCoordinator">
      <template v-if="!placementEndComponentIsPendingData && !placementEndComponentIsDraft">
        <router-link
          v-if="placementEndComponentCapabilityAssessmentComplete && placementEndComponentPerformanceObjectivesComplete && placementEndComponentOverallAssessmentComplete"
          class="btn btn-sm btn-success"
          :to="{ name: 'placements.single.end.manager.review', params: { placementId: placement.id, graduateId: placement.graduate_id } }"
        >View</router-link>
        <router-link
          v-else
          class="btn btn-sm btn-success"
          :to="{ name: 'placements.single.end.manager.performance', params: { placementId: placement.id, graduateId: placement.graduate_id } }"
        >Continue</router-link>
      </template>

      <ladda-button
        v-if="placementEndComponentIsSignedOff"
        class="btn btn-sm btn-danger ml-2"
        :click="handleRevertPlacement"
      >
        Revert Placement
      </ladda-button>
    </div>
  </div><!-- /.card -->
</template>

<script>
import { placementComputed, placementMethods, graduateComputed, graduateMethods } from 'vx/helpers'
import { helpers } from 'mixins/placements'
import { first } from 'lodash'
import { alert } from 'utils'

export default {
  mixins: [helpers],

  computed: {
    ...placementComputed,
    ...graduateComputed
  },

  methods: {
    ...placementMethods,
    ...graduateMethods,

    async handleRevertPlacement () {
      if (
        !await alert.confirm({
          title: 'Revert Finalised Placement?',
          text: 'Are you sure you want to revert this placement?'
        })
      ) {
        return
      }

      try {
        await this.revertEndComponent({ graduate: this.placement.graduate_id, placement: this.placement })

        this.setPlacementProperty({ property: 'end_component.status', value: 'review' })
        this.setPlacementProperty({ property: 'finalised', value: false })
        this.$noty.success('Successfully reverted')
      } catch (error) {
        this.$noty.error(first(this.errors.items).msg)
      }
    },

    async handleApprovePlacement () {
      if (
        !await alert.confirm({
          title: 'Finalise Placement?',
          text: 'Are you sure you want to finalise this placement?'
        })
      ) {
        return
      }

      try {
        await this.approveEndComponent({ graduate: this.placement.graduate_id, placement: this.placement })

        await this.setPlacementProperty({ property: 'end_component.status', value: 'signed_off' })

        await Promise.all([
          this.getGraduate(this.placement.graduate_id),
          this.getPlacement({
            graduate: this.placement.graduate_id,
            placement: this.placement
          })
        ])

        this.$noty.success('Successfully approved')
      } catch (error) {
        this.$noty.error(first(this.errors.items).msg)
      }
    }
  }
}
</script>
